import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Inject, Input, OnDestroy, Output, ViewEncapsulation } from '@angular/core';
import { MatSelectModule } from '@angular/material/select';
import { FormBuilder, FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { CACHE_KEYS } from '../../constants/cache-keys';
import { Router } from '@angular/router';
import { CacheService } from '../../services/cache.service';
import { SelectComponent } from '../general/select/select.component';
import { ButtonComponent } from '../general/button/button.component';
import { SelectOption } from '../general/select/select.model';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { OverlayContainer } from '@angular/cdk/overlay';
import { IconComponent } from '../general/icon/icon.component';
import { areDeepEqual } from '../../util/utils';
import { GetAllRolesResponse, LoginResults, LoginSelections } from '../../../login/login.viewmodel';
import { ContentFilterPipe } from '../../pipes/content-filter.pipe';
import { COMPONENT_META_DATA } from '../../constants/component-meta-data';
import { ContentModel } from '../../models/content.model';
import { FlexLayoutModule } from '@angular/flex-layout';
import { LoginService } from '../../../login/login.service';
import { PageService } from '../../services/page/page.service';
import { PageDataResolver } from '../../services/page/page-data-resolver.service';
import { ErrorData } from '../../models/error.model';
import { MatIconModule } from '@angular/material/icon';

@Component({
  selector: 'nuc-user-context-switcher',
  imports: [CommonModule, MatSelectModule, FormsModule, MatIconModule, ReactiveFormsModule, SelectComponent, ButtonComponent, FlexLayoutModule, ContentFilterPipe],
  templateUrl: './user-context-switcher.component.html',
  styleUrl: './user-context-switcher.component.scss',
  encapsulation: ViewEncapsulation.None
})
export class UserContextSwitcherComponent implements OnDestroy {

  loginOverlayForm: FormGroup = new FormGroup({});
  roleOptions: SelectOption[] = [];
  LABELS = COMPONENT_META_DATA.HEADER.LABELS.EDIT_USER_PREF_LABELS;
  pageContent: ContentModel[] = [];
  loginData: LoginResults;
  loginSelections: LoginSelections;
  constructor(private formBuilder: FormBuilder,
    protected router: Router, private cacheService: CacheService, private dialogRef: MatDialogRef<UserContextSwitcherComponent>, private overlayContainer: OverlayContainer, @Inject(MAT_DIALOG_DATA) public data: any,
    private loginService: LoginService, private pageService: PageService, private pageDataResolverService: PageDataResolver) {
    this.pageContent = this.data?.pageContentdata || [];
    this.addContextSwitcherOptionsCss();
  }

  ngOnInit(): void {
    this.loginData = this.cacheService.getFromCache(CACHE_KEYS.LOGIN.LOGIN_DATA);
    const dealerLists = this.loginData?.dealerLists || [];
    this.buildRoleOptions(dealerLists);
    this.init();
  }

  private buildRoleOptions(dealerLists: any[]) {
    const allRoles = dealerLists.flatMap((dealer: any) => dealer.roleList || []);
    const uniqueRolesMap = new Map();
    allRoles.forEach((role: any) => {
      if (!uniqueRolesMap.has(role.roleName)) {
        uniqueRolesMap.set(role.roleName, role);
      }
    });
    this.roleOptions = Array.from(uniqueRolesMap.values()).map((role: any) => {
      const option = new SelectOption(role.roleName, role.roleLabel);
      // Store businessOp data for payload updates
      (option as any).businessOp = role.businessOp;
      (option as any).businessOpLabel = role.businessOpLabel;
      return option;
    });
  }

  private init() {
    this.loginOverlayForm = this.formBuilder.group({
      role: new FormControl(null),
    });
    this.setInitialValues();
  }

  setInitialValues() {
    this.loginSelections = this.cacheService.getFromCache(CACHE_KEYS.LOGIN.LOGIN_SELECTIONS);
    if (!this.loginSelections) {
      return;
    }
    let lsro = this.loginSelections.selectedRole.value;
    let selectedRole = this.roleOptions?.find((ro) => ro.value == lsro);
    if (selectedRole) {
      this.getFormControl('role')?.setValue(selectedRole);
    }
  }

  addContextSwitcherOptionsCss() {
    const el = this.overlayContainer.getContainerElement();
    el.classList.add('context-switcher-options');
  }
  ngOnDestroy(): void {
    const el = this.overlayContainer.getContainerElement();
    el.classList.remove('context-switcher-options');
  }



  canSubmit() {
    let role = this.getFormControl('role')?.value;
    return !!role;
  }

  getFormControl(name: string): FormControl {
    return this.loginOverlayForm.get(name) as FormControl;
  }

  // close(state: boolean) {
  //   if (state) {
  //     state = this.saveSelections();
  //   }
  //   this.dialogRef.close(state);
  // }

  close(state: boolean) {
    let updatedSelections: LoginSelections | undefined = undefined;
    if (state) {
      state = this.saveSelections();
      updatedSelections = this.cacheService.getFromCache(CACHE_KEYS.LOGIN.LOGIN_SELECTIONS);
      
      // Get page path and call landing page API
      const pagePath = this.pageDataResolverService.getPagePath(COMPONENT_META_DATA.LANDING.KEY);
      console.log('UserContextSwitcher pagePath:', pagePath);
      this.pageService.getPageData(pagePath).subscribe({
        next: () => {
          console.log('Landing page API call successful');
          this.dialogRef.close({ state, updatedSelections });
        },
        error: (err) => {
          console.log('Landing page API call failed, closing dialog anyway');
          this.dialogRef.close({ state, updatedSelections });
        }
      });
      return; // Don't close yet, wait for API response
    }
    console.log('UserContextSwitcher close result:', { state, updatedSelections });
    this.dialogRef.close({ state, updatedSelections });
  }

  private saveSelections(): boolean {
    let prevSelections: LoginSelections = this.cacheService.getFromCache(CACHE_KEYS.LOGIN.LOGIN_SELECTIONS);
    let loginSelections: LoginSelections = new LoginSelections();
    // Preserve previous selection fields
    loginSelections.selectedDealer = prevSelections?.selectedDealer;
    loginSelections.region = prevSelections?.region;
    loginSelections.market = prevSelections?.market;
    loginSelections.isAdmin = prevSelections?.isAdmin;
    loginSelections.isIR = prevSelections?.isIR;
    // Update the role
    const selectedRoleOption = this.loginOverlayForm.get('role')?.value;
    loginSelections.selectedRole = selectedRoleOption;
    // Update businessUnit from selected role's businessOp
    if (selectedRoleOption && (selectedRoleOption as any).businessOp) {
      loginSelections.selectedBusinessLabel = new SelectOption(
        (selectedRoleOption as any).businessOp,
        (selectedRoleOption as any).businessOpLabel
      );
    } else {
      loginSelections.selectedBusinessLabel = prevSelections?.selectedBusinessLabel;
    }
    loginSelections.userId = this.loginData?.userPreferences?.userId;
    if (!areDeepEqual(prevSelections, loginSelections)) {
      this.cacheService.addToCache(CACHE_KEYS.LOGIN.LOGIN_SELECTIONS, loginSelections);
      return true;
    }
    return false;
  }






}
